/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
/* eslint-disable no-prototype-builtins */
/* eslint-disable camelcase */

/**
 * @class CartInsurance
 * @extends Alpine.Component
 *
 * @description
 * The CartInsurance class manages insurance products in the shopping cart.
 * It checks if the correct insurance variant is in the cart based on the
 * items present and replaces it if necessary. It also handles eligibility
 * checks and responds to changes in the cart.
 *
 * @property {boolean} open - Indicates whether the insurance modal is open.
 * @property {boolean} selected - Indicates if the current variant is selected.
 * @property {Array} variants - List of available insurance variants.
 * @property {Object} variant - The current selected insurance variant.
 * @property {Array} items - List of cart items that are eligible for insurance.
 * @property {number} total - The total value of eligible items in the cart.
 * @property {number} max_threshold - The maximum threshold value for the insurance.
 * @property {boolean} in_cart - Indicates if insurance is currently in the cart.
 * @property {boolean} replacing - Flag to prevent multiple replacements.
 * @property {boolean} eligible - Indicates if the cart is eligible for insurance.
 * @property {Object} eligible_types - Mapping of eligible product types for insurance.
 */
document.addEventListener('alpine:init', () => {
  Alpine.data('CartInsurance', (variants, excluded_handles) => ({
    open: false,
    selected: false,
    variants: [],
    variant: {},
    items: [],
    total: 0,
    max_threshold: 0,
    in_cart: false,
    replacing: false,
    eligible: false,
    /* prettier-ignore */
    eligible_types: {
      'Mattress': 1,
      'Bed Base': 1
    },
    excluded_handles,

    /**
     * Initializes the component, sets default values, and checks the cart.
     */
    init() {
      this.variants = this.sortVariants();
      this.variant = this.variants[0];
      this.max_threshold = this.setMaxThreshold();
      this.eligible = this.checkEligibilty(); // Trigger Eligibility Check On Load
      this.selected = this.inCart();

      this.checkCart();

      document.addEventListener('cart:cover:added', () => {
        this.eligible = this.checkEligibilty();
      });

      document.addEventListener('cart:change', () => {
        this.eligible = this.checkEligibilty();
        this.checkCart();
      });

      window.addEventListener('cart:cover:show-info', () => {
        this.inCart();
        this.open = true;
        const eventData = window.Simba.gtmConstructEventData('virtualPageView', {
          virtualPageViewValue: 'cartInsuranceInfoView'
        });
        Simba.gtmPushEvent(eventData);
      });

      this.$watch('open', () => {
        this.selected = this.inCart();
        document.querySelector('[data-ajax-cart] .ajax-cart-inner').classList.toggle('overflow-x-hidden');
        document.querySelector('[data-ajax-cart] .ajax-cart-inner').classList.toggle('overflow-hidden');
      });

      this.$watch('selected', (selected) => {
        if (!selected && this.inCart()) {
          this.remove();
        }
      });
    },

    /**
     * Getter for the insurance product variant price or return null if there is no variant or price (option is hidden).
     * @returns {number|null} The price of the variant or null if not available.
     */
    get variantPrice() {
      if (!this.variant || !this.variant.hasOwnProperty('price')) {
        return null;
      }
      return this.variant.price;
    },

    /**
     * Resets the insurance data and cart state.
     */
    reset() {
      this.setInsuranceData();
      this.setVariant();
      this.inCart();
    },

    /**
     * Retrieves the current insurance variant from the cart.
     *
     * @returns {Object|boolean} The current variant object or false if not found.
     */
    getCurrentVariantFromCart() {
      for (const item of Simba.Cart.items) {
        for (const variant of this.variants) {
          if (variant.id === item.variant_id) {
            return item;
          }
        }
      }
      return false;
    },

    /**
     * Checks if the insurance is currently in the cart.
     *
     * @returns {boolean} True if insurance is in the cart, false otherwise.
     */
    inCart() {
      this.in_cart = Simba.Cart.items.some((item) => this.variants.some((variant) => variant.id === item.variant_id));
      return this.in_cart;
    },

    /**
     * Sorts the available insurance variants based on their threshold.
     *
     * @returns {Array} The sorted list of variants.
     */
    sortVariants() {
      return variants.sort(function (a, b) {
        return a.threshold - b.threshold;
      });
    },

    /**
     * Sets the maximum threshold from the available variants.
     *
     * @returns {number} The maximum threshold value.
     */
    setMaxThreshold() {
      return Math.max.apply(
        null,
        this.variants.map(function (variant) {
          return variant.threshold;
        })
      );
    },

    /**
     * Gathers data on eligible items in the cart.
     */
    setInsuranceData() {
      this.items = [];
      this.total = 0;

      for (const item of Simba.Cart.items) {
        if (this.eligible_types[item.product_type] && !this.checkProductExcluded(item)) {
          this.total += item.price * item.quantity;

          if (this.items[item.product_type]) {
            this.items[item.product_type] += item.quantity;
          } else {
            this.items[item.product_type] = item.quantity;
          }
        }
      }
    },

    /**
     * Sets the current insurance variant based on the cart total.
     */
    setVariant() {
      this.variant = this.variants.filter((variant) => {
        if (this.total <= variant.threshold) {
          return true;
        }
      })[0];
    },

    /**
     * Check if the cart product is set to be excluded from eligibility.
     *
     * @param product
     * @returns {boolean}
     */
    checkProductExcluded(product) {
      return this.excluded_handles.some((handle) => handle === product.handle) || false;
    },

    /**
     * Checks if the cart is eligible for insurance.
     *
     * @returns {boolean} True if eligible, false otherwise.
     */
    checkEligibilty() {
      this.reset();

      // Exit If No Total Or Over Threshold
      if (this.total <= 0 || this.total > this.max_threshold) {
        return false;
      }

      // Check If We Have Eligible Quantity
      for (const product_type in this.items) {
        if (this.eligible_types.hasOwnProperty(product_type)) {
          const cart_item_amount = this.items[product_type];
          const eligible_item_amount = this.eligible_types[product_type];

          if (cart_item_amount >= eligible_item_amount) {
            return true;
          }
        }
      }

      // Not Eligible If We Get Here
      return false;
    },

    /**
     * Verifies if the correct insurance is in the cart and updates it.
     */
    checkCart() {
      this.reset();

      if (this.checkEligibilty() && this.inCart()) {
        const variant = this.getCurrentVariantFromCart();
        if (variant.id !== this.variant.id) {
          this.replace(variant, this.variant);
        }
      } else if (!this.checkEligibilty(true) && this.inCart()) {
        this.remove();
      }
    },

    /**
     * Adds the insurance to the cart.
     */
    add() {
      if (this.inCart()) return;

      const items = [
        {
          id: this.variant.id,
          quantity: 1
        }
      ];

      window.Simba.AjaxCart.cartCall(`${window.Simba.Lang.url}/cart/add.js`, 'POST', { items })
        .then(() => {
          window.Simba.AjaxCart.refresh(false);
          this.$dispatch('cart:cover:added');
        })
        .then(() => {
          this.open = false;
        });
    },

    /**
     * Removes the insurance from the cart.
     */
    remove() {
      const variant = this.getCurrentVariantFromCart();
      window.Simba.AjaxCart.cartCall(`${window.Simba.Lang.url}/cart/change.js`, 'POST', { id: variant.id, quantity: 0 }).then(() => {
        window.Simba.AjaxCart.refresh(false);
        this.$dispatch('cart:cover:removed');
      });
    },

    /**
     * Replaces the current insurance variant with another.
     *
     * @param {Object} current_variant - The current variant in the cart.
     * @param {Object} replacement_variant - The new variant to replace the current one.
     */
    replace(current_variant, replacement_variant) {
      if (this.replacing) return;
      this.replacing = true;

      const data = {};
      data.updates = {};
      data.updates[current_variant.id] = 0;
      data.updates[replacement_variant.id] = 1;

      window.Simba.AjaxCart.cartCall(`${window.Simba.Lang.url}/cart/update.js`, 'POST', data).then(() => {
        window.Simba.AjaxCart.refresh(false);
        this.$dispatch('cart:cover:replaced');
        this.replacing = false;
      });
    },

    /**
     * Dispatches an event to show insurance information.
     */
    showInfo() {
      this.$dispatch('cart:cover:show-info');
    }
  }));
});
